import { useContentMessages } from '@/features/editor/hooks/useContentMessages'
import { useLayoutEffect, useRef } from 'react'
import { Label, Textarea } from '@teach/ui'
import { useMessages } from '@/i18n'
import { messages } from './messages'

const MAX_DESC = 160
/** Не ниже четырёх строк: пустое поле не должно схлопываться в полоску. */
const MIN_HEIGHT = 104

interface Props {
  value: string
  onChange: (next: string) => void
  onBlur: () => void
  disabled: boolean
}

/**
 * «Короткое описание» урока. Поле растёт по тексту — весь текст виден целиком,
 * без внутренней прокрутки: панель урока сама прокручивается (`BlockPanel`),
 * места ей хватает. Высота считается по `scrollHeight`, а не CSS
 * `field-sizing`: Safari и Firefox его пока не умеют.
 */
export const LessonDescriptionField = ({ value, onChange, onBlur, disabled }: Props) => {
  const m = useMessages(messages)
  // Подсказки полей — на языке правки: видно, что правится английская версия.
  const cm = useContentMessages(messages)
  const ref = useRef<HTMLTextAreaElement>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.max(el.scrollHeight, MIN_HEIGHT)}px`
  }, [value])

  return (
    <div className="space-y-2">
      <Label className="text-foreground font-medium">{m.shortDescription}</Label>
      <Textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, MAX_DESC))}
        onBlur={onBlur}
        disabled={disabled}
        placeholder={cm.shortDescriptionPlaceholder}
        className="resize-none overflow-hidden rounded-xl"
        style={{ minHeight: MIN_HEIGHT }}
      />
      <p className="text-right text-xs text-muted-foreground">
        {value.length}/{MAX_DESC}
      </p>
    </div>
  )
}
